import type { FormikHelpers } from "formik";
import { Formik } from "formik";
import { useContext, useEffect, useState } from "react";
import {
	t2InitialValues,
	initialValues,
	validationSchema,
} from "./annualCropland";
import {
	useContextAwareGetAnnualCroplandDefaults,
	useContextAwareGetAnnualCropland,
	useUpdateAnnualCroplandMutation,
} from "@hooks/index";
import { selectCurrentProject } from "@features/project/projectSlice";
import {
	useAppSelector,
	useThreadsProcessor,
} from "@app/hooks";
import type { ModuleFull } from "./annualCroplandTypes";
import { BEModulesClassNames, FEModules } from "@utils/moduleList";
import GenericTierTwo from "../GenericTierTwo";
import GenericModule from "../GenericModule";
import useModuleHook from "../useModuleHook";
import useErrorMessage from "@utils/useErrorMessage";
import useAnnualCroplandSchema from "./useAnnualCroplandSchema";
import ModuleContext from "../../ModuleContext";
import { selectCurrentBuilder } from "@features/builder/builderSlice";

const AnnualCroplandModule = () => {
	const { activeActivityId } = useAppSelector(selectCurrentProject);
	const { belongsToLuc } = useContext(ModuleContext);
	const { moduleList } = useAppSelector(selectCurrentBuilder);

	const id = moduleList.find(({ id }) => id === FEModules.AnnualCropland)?.uniqueId;

	const {
		data: annualCroplandsData,
		isLoading,
		isError,
		error,
	} = useContextAwareGetAnnualCropland(id ?? 0, {
		refetchOnMountOrArgChange: true,
	});
	const [
		updateAnnualCropland,
		{ error: updateError, isError: isUpdateError, isLoading: isLoadingUpdate },
	] = useUpdateAnnualCroplandMutation();

	const { data: defaults, isError: isDefaultsError, error: defaultsError, isLoading: isLoadingDefaults } = useContextAwareGetAnnualCroplandDefaults(annualCroplandsData?.module.id ?? 0, { skip: !annualCroplandsData?.module.id });
	const { errorMsg: defaultsErrorMsg } = useErrorMessage({ isError: isDefaultsError, error: defaultsError });

	const { processModuleThreads } = useThreadsProcessor<ModuleFull>();
	const { moduleSchema, t2Schema } = useAnnualCroplandSchema();
	const [initValues, setInitValues] = useState<ModuleFull>({
		module: initialValues,
		tiertwo: t2InitialValues,
	});
	const { tabsOpen, errorMsg } = useModuleHook({
		skeletonsLoadingDeps: [isLoading, isLoadingDefaults],
		isMutateError: isUpdateError,
		mutateError: updateError,
	});
	const { errorMsg: blockingError } = useErrorMessage({ isError, error })

	useEffect(() => {
		if (annualCroplandsData) {
			setInitValues({ module: { ...annualCroplandsData.module, ...defaults }, tiertwo: { ...annualCroplandsData.tiertwo, ...defaults }, });
			processModuleThreads({
				module: annualCroplandsData.module,
			})
		}
	}, [annualCroplandsData, defaults, processModuleThreads]);

	const handleSubmit = async (
		values: ModuleFull,
		{ resetForm }: FormikHelpers<ModuleFull>,
	) => {
		const module = { ...values.module };
		const payload = {
			data: { ...values, module },
			activityId: activeActivityId ?? 0,
		};
		try {
			values.module.id && await updateAnnualCropland(payload).unwrap();;
			resetForm({ values });
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<Formik
			validationSchema={validationSchema(belongsToLuc)}
			initialValues={initValues}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			<>
				<GenericModule
					title="project.annual_cropland"
					t2Schema={t2Schema}
					moduleSchema={moduleSchema}
					isLoading={isLoadingUpdate}
					beModuleType={BEModulesClassNames.AnnualCropland}
					footerError={errorMsg}
					blockingError={blockingError}
					isError={isUpdateError}
				/>
				{!tabsOpen ? (
					<GenericTierTwo
						title="project.annual_cropland"
						t2Schema={t2Schema}
						tabsOpen={tabsOpen}
						warningMessage={defaultsErrorMsg}
					/>
				) : null}
			</>
		</Formik>
	);
};

export default AnnualCroplandModule;
